import { ChatSupportStore } from '@/features/support/ChatSupportStore'
import type { Observable } from 'rxjs'
import type { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import { filterNonNil } from '@/utils/rx-util'
import { HubSpotTrackingApi } from '@/features/support/api/hubspot-tracking-api'
import Env from '@/infra/env'

export interface SupportFeatureOptions {
  token$: Observable<string>
  getCurrentMember$: Observable<WorkspaceMember>
}

/**
 * Opens the support chat.
 */
export type OpenSupportChatFn = () => unknown

/**
 * Whether external support is enabled.
 */
export type IsSupportEnabledFn = () => boolean

/**
 * Whether the HubSpot widget failed to load (e.g. blocked by an ad blocker).
 */
export type IsWidgetBlockedFn = () => boolean

/**
 * Manages integration with whatever Customer Support chat we're using.
 */
export type SupportFeature = ReturnType<typeof SupportFeature>

export function SupportFeature(options: SupportFeatureOptions) {
  const freshBooksApi = new HubSpotTrackingApi({
    token$: options.token$,
    baseURL: Env.FRESHBOOKS_API,
  })

  const chatSupportStore = new ChatSupportStore(
    options.getCurrentMember$.pipe(filterNonNil()),
    freshBooksApi,
  )

  const openSupportChat: OpenSupportChatFn = () =>
    chatSupportStore.openSupportChat()

  const isSupportEnabled: IsSupportEnabledFn = () =>
    chatSupportStore.isSupportEnabled

  const isWidgetBlocked: IsWidgetBlockedFn = () =>
    chatSupportStore.isWidgetBlocked

  return {
    // Internal
    chatSupportStore,

    // Public
    openSupportChat,
    isSupportEnabled,
    isWidgetBlocked,
  }
}
